import { ModalBody, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import useFetchTrigger from 'components/domain/automation/triggers/useFetchTrigger';
import useFetchTriggerOptions from 'components/domain/automation/triggers/useFetchTriggerOptions';
import useFetchLeadChannels from 'components/domain/lead/useFetchLeadChannels';
import useFetchPropertiesForMultiSelect from 'components/forms/domain/property/PropertyMultiSelectField/useFetchPropertiesForMultiSelect';
import FormFieldset from 'components/forms/form/FormFieldset';
import LoadingPlaceholder from 'components/loader/LoadingPlaceholder';
import { Trigger } from 'models/automation/Triggers';
import {
  getFormDefaultValues,
  triggerAddEditModalSchema,
} from './TriggerAddEditModal.schema';
import { FormWithProviderStyled } from './TriggerAddEditModal.styles';
import { TriggerAddEditModalContextProvider } from './TriggerAddEditModalContext';
import TriggerAddEditModalEventTypeField from './TriggerAddEditModalEventTypeField';
import TriggerAddEditModalFooter from './TriggerAddEditModalFooter';
import TriggerAddEditModalNameField from './TriggerAddEditModalNameField';
import TriggerAddEditModalTimeConditionField from './TriggerAddEditModalTimeConditionField';
import TriggerAddEditModalTriggerTypeField from './TriggerAddEditModalTriggerTypeField';
import TriggerAddEditModalActionTypeExtraFields from './actions/TriggerAddEditModalActionTypeExtraFields';
import TriggerAddEditModalActionTypeField from './actions/TriggerAddEditModalActionTypeField';
import TriggerAddEditModalConditionsField from './conditions/TriggerAddEditModalConditionsField';
import useTriggerAddEditModalFormSubmit from './useTriggerAddEditModalFormSubmit';

interface TriggerAddEditModalProps {
  closeModal: () => void;
  triggerUid?: string;
  originalTrigger?: Trigger | Omit<Trigger, 'uid'>;
}

const TriggerAddEditModal = ({
  closeModal,
  triggerUid,
  originalTrigger,
}: TriggerAddEditModalProps) => {
  const { t } = useTranslation();

  const { data: fetchedTrigger, isLoading: isLoadingTrigger } = useFetchTrigger(
    { triggerUid, enabled: !!triggerUid },
  );

  const trigger = originalTrigger ?? fetchedTrigger;

  const { onSubmit } = useTriggerAddEditModalFormSubmit({
    originalTrigger: trigger,
    closeModal,
  });

  const { data: triggerOptions } = useFetchTriggerOptions();

  const { data: leadChannels, isLoading: isLoadingLeadChannels } =
    useFetchLeadChannels();

  const { allProperties, isFetching: isFetchingAllProperties } =
    useFetchPropertiesForMultiSelect();

  const isDuplication = !!trigger && !('uid' in trigger);
  const isEditing = !!triggerUid || (!!trigger && 'uid' in trigger);

  if (
    isLoadingLeadChannels ||
    isFetchingAllProperties ||
    (triggerUid && isLoadingTrigger)
  ) {
    return <LoadingPlaceholder />;
  }

  const emptyDefaultValue = getFormDefaultValues({
    allLeadChannels: leadChannels?.leadChannels,
    allProperties,
    t,
    triggerOptions,
  });

  const defaultValues = getFormDefaultValues({
    allLeadChannels: leadChannels?.leadChannels,
    allProperties,
    t,
    trigger,
    triggerOptions,
  });

  return (
    <FormWithProviderStyled
      defaultValues={isEditing ? defaultValues : emptyDefaultValue}
      onSubmit={onSubmit}
      resolver={triggerAddEditModalSchema({ t })}
      noValidate
      dirtyChecker
    >
      <FormFieldset>
        <ModalBody>
          <Row className="d-flex flex-column">
            <TriggerAddEditModalContextProvider>
              <TriggerAddEditModalNameField />
              <TriggerAddEditModalTriggerTypeField />
              <TriggerAddEditModalEventTypeField />
              <TriggerAddEditModalTimeConditionField />
              <TriggerAddEditModalConditionsField />
              <TriggerAddEditModalActionTypeField />
              <TriggerAddEditModalActionTypeExtraFields />
            </TriggerAddEditModalContextProvider>
          </Row>
        </ModalBody>
        <TriggerAddEditModalFooter
          closeModal={closeModal}
          initializeWith={isDuplication ? defaultValues : undefined}
        />
      </FormFieldset>
    </FormWithProviderStyled>
  );
};

export default TriggerAddEditModal;
